import {
    MutationOptions,
    useMutation,
    useQueryClient,
    useQuery,
    QueryResult,
    UseMutationResult,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    AccountId,
    AccountInterest,
    AccountNumber,
    LoanApplication,
    LoanDetails,
    LoanPayment,
    LoanStakeholder,
} from '../generated-types';
import {
    getAllLoanPayments,
    getLoanDetails,
    getLoanInterests,
    updateLoanAccountDebitAccountNumber,
    updateLoanAccountPaymentDay,
    getLoanApplications,
    getLoanStakeholders,
} from '../services/loanService';

const LOAN_DETAILS_KEY = 'loan-details';

export const useRefreshLoanDetails = () => {
    const queryClient = useQueryClient();

    return () => {
        return queryClient.invalidateQueries({ queryKey: [LOAN_DETAILS_KEY] });
    };
};

export const useLoanDetails = (
    loanAccountId?: AccountId,
    enabled: boolean = true,
): QueryResult<LoanDetails | undefined> =>
    useQuery({
        queryKey: [LOAN_DETAILS_KEY, loanAccountId?.value],
        queryFn: () => getLoanDetails(loanAccountId!),
        enabled: !!loanAccountId && enabled,
    });

export const useLoanDetailMutation = (
    options?: Partial<MutationOptions<LoanDetails | undefined, RequestExceptionWithResponse, AccountId>>,
): UseMutationResult<LoanDetails | undefined, RequestExceptionWithResponse, AccountId> => {
    const queryClient = useQueryClient();
    return useMutation({
        ...options,
        mutationKey: [LOAN_DETAILS_KEY],
        mutationFn: async (loanAccountId: AccountId) => {
            const queryData = queryClient.getQueryData<LoanDetails | undefined>([
                LOAN_DETAILS_KEY,
                loanAccountId.value,
            ]);
            if (queryData) {
                return queryData;
            }
            const loanDetails = await getLoanDetails(loanAccountId);
            queryClient.setQueryData([LOAN_DETAILS_KEY, loanAccountId.value], loanDetails);
            return loanDetails;
        },
    });
};

export const useLoanInterest = (loanAccountId?: AccountId): QueryResult<AccountInterest | undefined> =>
    useQuery({
        queryKey: ['loan-interest', loanAccountId?.value],
        queryFn: () => getLoanInterests(loanAccountId!),
        enabled: !!loanAccountId,
    });

export interface UpdateLoanDebitAccountRequest {
    accountId: AccountId;
    newAccountNumber: AccountNumber;
}

export const useUpdateLoanDebitAccountNumber = () =>
    useMutation<void, RequestExceptionWithResponse, UpdateLoanDebitAccountRequest>({
        mutationKey: ['useUpdateLoanDebitAccountNumber'],
        mutationFn: (request) => updateLoanAccountDebitAccountNumber(request.accountId, request.newAccountNumber),
    });

export interface UpdateLoanPaymentDate {
    accountId: AccountId;
    paymentDay: number;
    paymentDayNextMonth?: boolean;
}

export const useUpdateLoanPaymentDay = () =>
    useMutation<void, RequestExceptionWithResponse, UpdateLoanPaymentDate>({
        mutationKey: ['useUpdateLoanPaymentDay'],
        mutationFn: (request) =>
            updateLoanAccountPaymentDay(request.accountId, request.paymentDay, request.paymentDayNextMonth),
    });

export const useAllLoanPayments = (enabled = false): QueryResult<LoanPayment[]> =>
    useQuery({
        queryKey: ['loan-payments'],
        queryFn: getAllLoanPayments,
        enabled,
    });

export const useLoanApplications = (enabled = false): QueryResult<LoanApplication[]> =>
    useQuery({
        queryKey: ['loan-applications'],
        queryFn: getLoanApplications,
        enabled,
    });

export const useLoanStakeholders = (loanAccountId?: AccountId, enabled = false): QueryResult<LoanStakeholder[]> =>
    useQuery({
        queryKey: ['loan-stakeholders', loanAccountId?.value],
        queryFn: () => getLoanStakeholders(loanAccountId!),
        enabled: enabled && !!loanAccountId,
    });
